import { useState, useEffect, useRef, useMemo } from 'react';
import { CKEditor, useCKEditorCloud } from '@ckeditor/ckeditor5-react';
import './app.css';
import { EditorConfig } from 'ckeditor5';
import { MyCustomUploadAdapterPlugin } from './MyUploadAdapter';
import { ErrorUploadImage } from './ErrorUploadImage';
import { message } from 'antd';

const LICENSE_KEY = import.meta.env.VITE_LICENSE_KEY_CK;

export default function CKEditorMaster({
  content,
  handleUpdateContent,
  isDisabled = false,
  MAX_CHAR_LENGTH = 10000000,
  placeholder = 'Type or paste your content here!'
}: {
  content: any;
  handleUpdateContent: (data: any) => void;
  isDisabled?: boolean;
  MAX_CHAR_LENGTH?: number;
  placeholder?: string;
}) {
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const cloud = useCKEditorCloud({ version: '47.1.0', translations: ['vi'] });
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);

  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  const { ClassicEditor, editorConfig } = useMemo(() => {
    if (cloud.status !== 'success' || !isLayoutReady) {
      return {};
    }

    const {
      ClassicEditor,
      Alignment,
      Autoformat,
      AutoImage,
      Autosave,
      BalloonToolbar,
      BlockQuote,
      Bold,
      Code,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HtmlEmbed,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsert,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      Mention,
      Paragraph,
      PasteFromOffice,
      RemoveFormat,
      Strikethrough,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      TodoList,
      Underline,
      FileRepository,
      Emoji
    } = cloud.CKEditor;

    return {
      ClassicEditor,
      editorConfig: {
        toolbar: {
          items: [
            'heading',
            '|',
            'fontSize',
            'fontFamily',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'subscript',
            'superscript',
            'code',
            'removeFormat',
            '|',
            'link',
            'insertImage',
            'mediaEmbed',
            'insertTable',
            'highlight',
            'emoji',
            'blockQuote',
            'htmlEmbed',
            '|',
            'alignment',
            '|',
            'bulletedList',
            'numberedList',
            'todoList',
            'outdent',
            'indent',
            'undo',
            'redo'
          ],
          shouldNotGroupWhenFull: true
        },
        plugins: [
          FileRepository,
          MyCustomUploadAdapterPlugin,
          Alignment,
          Autoformat,
          AutoImage,
          Autosave,
          BalloonToolbar,
          BlockQuote,
          Bold,
          Code,
          Essentials,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          GeneralHtmlSupport,
          Heading,
          Highlight,
          HtmlEmbed,
          ImageBlock,
          ImageCaption,
          ImageInline,
          ImageInsert,
          ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          Italic,
          Link,
          LinkImage,
          List,
          ListProperties,
          MediaEmbed,
          Mention,
          Paragraph,
          PasteFromOffice,
          RemoveFormat,
          Strikethrough,
          Subscript,
          Superscript,
          Table,
          TableCaption,
          TableCellProperties,
          TableColumnResize,
          TableProperties,
          TableToolbar,
          TextTransformation,
          TodoList,
          Underline,
          Emoji
        ],
        balloonToolbar: [
          'bold',
          'italic',
          '|',
          'link',
          'insertImage',
          '|',
          'bulletedList',
          'numberedList'
        ],
        fontFamily: {
          supportAllValues: true
        },
        fontSize: {
          options: [10, 12, 14, 'default', 18, 20, 22],
          supportAllValues: true
        },
        heading: {
          options: [
            {
              model: 'paragraph',
              title: 'Paragraph',
              class: 'ck-heading_paragraph'
            },
            {
              model: 'heading1',
              view: 'h1',
              title: 'Heading 1',
              class: 'ck-heading_heading1'
            },
            {
              model: 'heading2',
              view: 'h2',
              title: 'Heading 2',
              class: 'ck-heading_heading2'
            },
            {
              model: 'heading3',
              view: 'h3',
              title: 'Heading 3',
              class: 'ck-heading_heading3'
            },
            {
              model: 'heading4',
              view: 'h4',
              title: 'Heading 4',
              class: 'ck-heading_heading4'
            },
            {
              model: 'heading5',
              view: 'h5',
              title: 'Heading 5',
              class: 'ck-heading_heading5'
            },
            {
              model: 'heading6',
              view: 'h6',
              title: 'Heading 6',
              class: 'ck-heading_heading6'
            }
          ]
        },
        htmlSupport: {
          allow: [
            {
              name: /^.*$/,
              styles: true,
              attributes: true,
              classes: true
            }
          ]
        },
        image: {
          toolbar: [
            'toggleImageCaption',
            'imageTextAlternative',
            '|',
            'imageStyle:inline',
            'imageStyle:wrapText',
            'imageStyle:breakText',
            '|',
            'resizeImage'
          ]
        },
        initialData: content,
        language: 'vi',
        licenseKey: LICENSE_KEY,
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://',
          decorators: {
            toggleDownloadable: {
              mode: 'manual',
              label: 'Downloadable',
              attributes: {
                download: 'file'
              }
            }
          }
        },
        list: {
          properties: {
            styles: true,
            startIndex: true,
            reversed: true
          }
        },
        mention: {
          feeds: [
            {
              marker: '@',
              feed: [
                /* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
              ]
            }
          ]
        },
        placeholder: placeholder || 'Type or paste your content here!',
        table: {
          contentToolbar: [
            'tableColumn',
            'tableRow',
            'mergeTableCells',
            'tableProperties',
            'tableCellProperties'
          ]
        }
      } as EditorConfig
    };
  }, [cloud, isLayoutReady, content, placeholder]);

  const getPlainTextLength = (editor: any) => {
    const root = editor.model.document.getRoot();
    let text = '';

    for (const child of root.getChildren()) {
      if ('getChildren' in child) {
        for (const grandChild of child.getChildren()) {
          if ('data' in grandChild) {
            text += grandChild.data;
          }
        }
      } else if ('data' in child) {
        text += child.data;
      }
    }

    return text.length;
  };

  const handleEditorChange = (_: any, editor: any) => {
    const plainTextLength = getPlainTextLength(editor);
    const htmlData = editor.getData();

    if (plainTextLength > MAX_CHAR_LENGTH) {
      if (!isLimitExceeded) {
        setIsLimitExceeded(true);
        message.destroy();
        message.error(`Vượt quá giới hạn ${MAX_CHAR_LENGTH} ký tự`);
      }

      editor.execute('undo');
      return;
    }

    if (isLimitExceeded) {
      setIsLimitExceeded(false);
    }

    handleUpdateContent(htmlData);
  };

  return (
    <div className='main-container'>
      <div
        className='editor-container editor-container_classic-editor'
        ref={editorContainerRef}
      >
        <div className='editor-container__editor'>
          <div ref={editorRef}>
            {ClassicEditor && editorConfig && (
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                disabled={isDisabled}
                onReady={(editor) => {
                  if (!editor || !editor.editing) return;

                  // Ngăn người dùng gõ thêm nếu đã vượt giới hạn
                  editor.editing.view.document.on(
                    'keydown',
                    (evt: any, data: any) => {
                      const allowedKeys = [
                        'Backspace',
                        'Delete',
                        'ArrowLeft',
                        'ArrowRight',
                        'ArrowUp',
                        'ArrowDown'
                      ];
                      if (isLimitExceeded && !allowedKeys.includes(data.key)) {
                        evt.stop();
                        message.destroy();
                        message.error(
                          `Vượt quá giới hạn ${MAX_CHAR_LENGTH} ký tự`
                        );
                      }
                    },
                    { priority: 'high' }
                  );
                }}
                onChange={handleEditorChange}
                onError={(e) => {
                  console.log('Error: ', e, e instanceof ErrorUploadImage);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
